import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, CheckCircle2, Mail, Sparkles, User } from "lucide-react";
import Aurora from "@/components/Aurora";
import { createLead } from "@/lib/api";

export default function StayInformed() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setStatus("loading");
    setErrMsg("");
    try {
      await createLead({ email: email.trim(), name: name.trim(), source: "stay_informed" });
      setStatus("success");
      setName("");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrMsg(
        Array.isArray(detail)
          ? detail[0]?.msg || "Please enter a valid email."
          : detail || "Something went wrong. Please try again."
      );
      setStatus("error");
    }
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden grid place-items-center px-5 py-16">
      <Aurora variant="intense" rings />
      <div className="relative max-w-md w-full">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <span className="inline-flex w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 grid place-items-center shadow-[0_0_24px_rgba(0,229,255,0.5)]">
            <Sparkles className="w-4 h-4 text-black" strokeWidth={2.5} />
          </span>
          <span className="font-display text-white text-lg">
            <span className="font-normal">Favor</span>{" "}
            <span className="font-bold">Studio.</span>
          </span>
        </Link>

        {status === "success" ? (
          <div className="glass-card p-7 sm:p-9 flex items-start gap-4">
            <CheckCircle2 className="w-6 h-6 text-cyan-300 mt-1 shrink-0" />
            <div>
              <div className="label-chip mb-4"><span className="dot" /><span>YOU'RE IN</span></div>
              <h1 className="font-display text-3xl sm:text-4xl">
                <span className="font-normal">You&apos;re </span>
                <span className="font-bold">Confirmed.</span>
              </h1>
              <p className="text-zinc-400 mt-3 text-sm leading-relaxed">
                Watch your inbox for updates from Favor Studio — course news, pricing announcements, AI strategies, and future releases.
              </p>
            </div>
          </div>
        ) : (
          <div className="glass-card p-7 sm:p-9">
            <div className="label-chip"><span className="dot" /><span>STAY INFORMED</span></div>
            <h1 className="font-display text-3xl sm:text-4xl mt-5">
              <span className="font-normal">Stay </span>
              <span className="font-bold">Informed.</span>
            </h1>
            <p className="mt-3 text-zinc-400 text-sm leading-relaxed">
              Be the first to hear about new AI Employees, course updates, implementation strategies, and future releases from Favor Studio.
            </p>

            <form onSubmit={onSubmit} className="mt-7 space-y-4">
              <div>
                <label className="text-[11px] tracking-[0.25em] uppercase text-zinc-500">Full Name</label>
                <div className="mt-1.5 relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    required
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-transparent border border-white/10 focus:border-cyan-400/60 focus:outline-none rounded-full pl-11 pr-5 py-3 text-white placeholder:text-zinc-500 text-sm transition-colors"
                    maxLength={120}
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] tracking-[0.25em] uppercase text-zinc-500">Email Address</label>
                <div className="mt-1.5 relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="email"
                    required
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent border border-white/10 focus:border-cyan-400/60 focus:outline-none rounded-full pl-11 pr-5 py-3 text-white placeholder:text-zinc-500 text-sm transition-colors"
                    maxLength={200}
                  />
                </div>
              </div>
              {errMsg && (
                <p className="text-rose-300 text-sm">{errMsg}</p>
              )}
              <button
                type="submit"
                disabled={status === "loading"}
                className="btn-aurora w-full disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {status === "loading" ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> SENDING…</>
                ) : (
                  <>NOTIFY ME <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </form>
          </div>
        )}

        <p className="text-center text-xs text-zinc-500 mt-6">
          No spam. Unsubscribe anytime.
        </p>
      </div>
    </div>
  );
}
